import React, { useEffect, useState } from 'react';

import type { ArticleMedia } from 'common/components/image-carousel/types';
import { mapResourcesToMediaItems } from 'common/components/ImageGalleryOverlay/mediaResourceMapper';
import { HeadlineH8 } from 'common/components/Typography';
import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { useHiddenBodyOverflow } from 'common/hooks/scroll';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { CarouselItemProps } from 'common/types/carousel-item';
import { transformImageUrl } from 'common/utils/cdn/image';
import Picture from '../Picture';

interface PicturesProps {
  items: string[];
}

interface ImageGalleryCloseAction {
  isOpen: false;
}

export interface ImageGalleryOpenAction {
  isOpen: true;
  items: ArticleMedia[];
  thumbnails: CarouselItemProps[];
  activeItem: number;
  toggleHandler: () => void;
}

export type ImageGalleryToggleAction = ImageGalleryCloseAction | ImageGalleryOpenAction;

const THUMBNAIL_SIZE = 32;
const GALLERY_THUMBNAIL_SIZE = 70;

const getThumbnailImages = (images: string[]) =>
  images.map(src => ({
    image: { src: transformImageUrl(src, { width: GALLERY_THUMBNAIL_SIZE }), alt: '' },
  }));

const useGalleryOverlay = (images: string[]) => {
  const [activeOverlayItem, setActiveOverlayItem] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const toggleOverlayHandler = () => setIsOverlayOpen(isOpen => !isOpen);

  useEffect(() => {
    eventBus.emit<ImageGalleryToggleAction>(EventBusEvent.ReclamationImageGalleryToggle, {
      isOpen: isOverlayOpen,
      ...(isOverlayOpen && {
        items: mapResourcesToMediaItems({
          images: images.map(image => ({ path: image })),
          videos: [],
          alt: '',
        }),
        thumbnails: getThumbnailImages(images),
        activeItem: activeOverlayItem,
        toggleHandler: toggleOverlayHandler,
      }),
    } as ImageGalleryToggleAction);
  }, [isOverlayOpen]);

  const imageClickHandler = (index: number) => {
    setIsOverlayOpen(true);
    setActiveOverlayItem(index);
  };

  useHiddenBodyOverflow(isOverlayOpen);

  return imageClickHandler;
};

export const PicturesHeading = () => {
  const { t } = useLocalization();

  return <HeadlineH8 as="h2">{t('h24_images_attached')}:</HeadlineH8>;
};

const Pictures = ({ items }: PicturesProps) => {
  const imageClickHandler = useGalleryOverlay(items);

  return (
    !!items.length && (
      <section data-testid="pictures">
        {items.map((src, index) => (
          <Picture
            key={src}
            src={src}
            size={THUMBNAIL_SIZE}
            index={index}
            onClick={imageClickHandler}
          />
        ))}
      </section>
    )
  );
};

export default Pictures;
